// @ts-strict-ignore
import * as React from 'react';
import routes from '../routes';
import { Route, RouteComponentProps, Switch, useParams } from 'react-router-dom';
import Integrations from './Integrations';
import { ValidatedAppRouteWrapper, ValidatedIntegrationRedirectRoute } from './ValidatedRouteComponents';
import { PageViewTrackingWrapper } from '../helpers/PageViewTrackingWrapper';
import AppSettings from './AppSettings';
import ErrorBoundary from './ErrorBoundary';
import SetReleaseNoteDefaultsComponent from './appSettings/SetReleaseNoteDefaultsComponent';
import {
    AppComponentProps,
    AppRouteComponentMatchParams,
    SettingsTypeComponentMatchParams,
} from '../typescriptHelpers';
import ReleaseScheduleSettings from './appSettings/ReleaseScheduleSettings';
import { AppPlatform, SettingType } from '../types';
import { AutomationsComponent } from './appSettings/Automations';
import ReleaseScheduleSettingsComponent from './appSettings/ReleaseScheduleSettingsComponent';
import { ChecklistItemsComponent } from './ChecklistItemsComponent';
import AppNotifications from './appSettings/AppNotifications';
import AppStoreSettingsComponent from './appSettings/AppStoreSettingsComponent';
import { descriptionForSetting, displayNameForSetting } from '../helpers/displayHelpers';
import TeamRoles from './appSettings/TeamRoles';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { selectAppName, selectAppPlatform, selectAppStoreType } from '../selectors/selectors';
import Monitoring from './appSettings/Monitoring';
import PageLayout from './SettingsPageLayout';
import { CustomMessagesSettings } from './appSettings/CustomMessagesSettings';
import { createAppSettingsRouteWithSettingType } from '../helpers/routeHelpers';
import { ProfilesAndDevicesOrSigningKeys } from './appSettings/ProfilesAndDevices/ProfilesAndDevicesOrSigningKeys';
import { isIosPlatform } from '../helpers';
import ReleasePilotRotationComponent from './appSettings/ReleasePilotRotationComponent';
import { SetupTasks } from '@/components/appSettings/SetupTasks';
import { WorkflowStepsSettings } from './appSettings/WorkflowStepsSettings';
import EntityNotFoundComponent from './EntityNotFoundComponent';
import { DestinationsSettings } from './appSettings/DestinationsSettings';
import { trackAppRouteEvent } from '@/actions/trackEvent';
import { Event } from '@/utils/analytics';

const AppSettingsContainer: React.FC<AppComponentProps> = (props) => {
    const appName = useSelector((state: RootState) => selectAppName(state, props.appId));
    const platform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));

    return (
        <ErrorBoundary showRedirectLink={true}>
            <Switch>
                <ValidatedIntegrationRedirectRoute
                    exact
                    path={routes.integrationRedirect}
                    component={(props) => (
                        <PageLayout
                            component={Integrations}
                            title={displayNameForSetting(SettingType['Integrations'])}
                            description={descriptionForSetting(
                                SettingType.Integrations,
                                appStoreType,
                                appName,
                                platform,
                            )}
                            {...props}
                        />
                    )}
                />
                <Route
                    path={routes.integrations}
                    render={(routeProps: RouteComponentProps) => {
                        const appRouteProps = routeProps as RouteComponentProps<AppRouteComponentMatchParams>;

                        return (
                            <ValidatedAppRouteWrapper {...appRouteProps}>
                                {(validatedAppComponentProps) => (
                                    <PageViewTrackingWrapper pageTitle={'Integrations'}>
                                        <PageLayout
                                            component={Integrations}
                                            title={displayNameForSetting(SettingType['Integrations'])}
                                            description={descriptionForSetting(
                                                SettingType.Integrations,
                                                appStoreType,
                                                appName,
                                                platform,
                                            )}
                                            {...validatedAppComponentProps}
                                            {...appRouteProps}
                                        />
                                    </PageViewTrackingWrapper>
                                )}
                            </ValidatedAppRouteWrapper>
                        );
                    }}
                />
                <Route
                    exact
                    path={routes.appSettings}
                    render={(routeProps: RouteComponentProps) => {
                        const appRouteProps = routeProps as RouteComponentProps<AppRouteComponentMatchParams>;

                        return (
                            <ValidatedAppRouteWrapper {...appRouteProps}>
                                {(validatedAppComponentProps) => (
                                    <PageLayout
                                        component={AppSettings}
                                        title={displayNameForSetting(SettingType['General'])}
                                        description={descriptionForSetting(
                                            SettingType.General,
                                            appStoreType,
                                            appName,
                                            platform,
                                        )}
                                        {...validatedAppComponentProps}
                                    />
                                )}
                            </ValidatedAppRouteWrapper>
                        );
                    }}
                />
                <Route
                    path={routes.appSettingsType}
                    render={(routeProps: RouteComponentProps) => {
                        const appRouteProps = routeProps as RouteComponentProps<AppRouteComponentMatchParams>;

                        return (
                            <ValidatedAppRouteWrapper {...appRouteProps}>
                                {(validatedAppComponentProps) => <Settings {...validatedAppComponentProps} />}
                            </ValidatedAppRouteWrapper>
                        );
                    }}
                />
            </Switch>
        </ErrorBoundary>
    );
};

const settingComponentForPlatform = (platform: AppPlatform) => {
    return new Map([
        [SettingType.General, AppSettings],
        [SettingType.Destinations, DestinationsSettings],
        [SettingType.ReleasePilotRotation, ReleasePilotRotationComponent],
        [SettingType.Team, TeamRoles],
        [SettingType.Automations, AutomationsComponent],
        [SettingType.Schedule, ReleaseScheduleSettingsComponent],
        [SettingType.AppStore, AppStoreSettingsComponent],
        [SettingType.HealthMetrics, Monitoring],
        isIosPlatform(platform) ?
            [SettingType.ProfilesAndDevices, ProfilesAndDevicesOrSigningKeys]
        :   [SettingType.SigningKeys, ProfilesAndDevicesOrSigningKeys],
        [SettingType.Checklists, ChecklistItemsComponent],
        [SettingType.Notifications, AppNotifications],
        [SettingType.CustomStrings, CustomMessagesSettings],
        [SettingType.DefaultReleaseNotes, SetReleaseNoteDefaultsComponent],
        [SettingType.ScheduleCadence, ReleaseScheduleSettings],
        [SettingType.SetupTasks, SetupTasks],
        [SettingType.WorkflowSteps, WorkflowStepsSettings],
    ]);
};

const backRouteForSettingType = (orgIdParam: string, appIdParam: string, settingType: SettingType) => {
    switch (settingType) {
        case SettingType.DefaultReleaseNotes:
            return createAppSettingsRouteWithSettingType(orgIdParam, appIdParam, SettingType.AppStore);
        case SettingType.ScheduleCadence:
            return createAppSettingsRouteWithSettingType(orgIdParam, appIdParam, SettingType.Schedule);
        default:
            return null;
    }
};

const Settings = (props: AppComponentProps) => {
    const dispatch = useDispatch();
    const params = useParams<SettingsTypeComponentMatchParams>();

    const activeSetting = params.settingType ?? SettingType.General;

    const appName = useSelector((state: RootState) => selectAppName(state, props.appId));
    const platform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const backNavigationRoute = backRouteForSettingType(params.orgIdParam, params.appIdParam, activeSetting);

    React.useEffect(() => {
        if (activeSetting === SettingType.Destinations) {
            dispatch(
                trackAppRouteEvent({
                    orgId: props.orgId,
                    appId: props.appId,
                    eventName: Event.ViewTagManagement,
                    viewType: 'destinations',
                }),
            );
        }
    }, [activeSetting, dispatch, props.appId, props.orgId]);

    const settingComponent = settingComponentForPlatform(platform);
    return (
        <PageLayout
            component={
                settingComponent?.get(activeSetting) ??
                (() => (
                    <div
                        style={{
                            position: 'absolute',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            top: 0,
                            right: 0,
                        }}
                    >
                        <EntityNotFoundComponent entityType="Page" />
                    </div>
                ))
            }
            title={displayNameForSetting(activeSetting)}
            description={descriptionForSetting(activeSetting, appStoreType, appName, platform)}
            backNavigationRoute={backNavigationRoute}
            {...props}
        />
    );
};

export default AppSettingsContainer;
